import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP, formatFecha, hoyISO } from '../lib/format';
import { q, crearMovimiento, recalcularDeuda, borrarMovimientoSimple } from '../lib/vinculos';
import { useAccion } from '../lib/useAccion';
import ErrorBanner from './ErrorBanner';

const ICONOS = ['💳', '🚗', '🏠', '📱', '🤝', '🏦', '🛍️', '💊', '📚', '✈️'];

function ProgressBar({ pagado, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((pagado / total) * 100)) : 0;
  const color = pct >= 75 ? '#22c55e' : pct >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{ margin: '10px 0 4px' }}>
      <div style={{ background: 'var(--border)', borderRadius: 99, height: 8, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width 0.4s ease' }} />
      </div>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{pct}% pagado</span>
    </div>
  );
}

const formVacio = { nombre: '', acreedor: '', icono: '💳', monto_original: '', cuota_mensual: '', dia_vencimiento: '', notas: '' };

function Deudas({ userId }) {
  const [deudas, setDeudas] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [bolsillos, setBolsillos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [pagoModal, setPagoModal] = useState(null); // deuda object
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const { saving, error, setError, run } = useAccion();

  const [form, setForm] = useState(formVacio);
  const [formPago, setFormPago] = useState({ monto: '', bolsillo_id: '', fecha: hoyISO(), nota: '' });
  const [editForm, setEditForm] = useState({});

  const loadData = useCallback(async () => {
    if (!userId) return;
    const [d, p, b] = await Promise.all([
      q(supabase.from('deudas').select('*').eq('user_id', userId).order('created_at')),
      q(supabase.from('pagos_deuda').select('*').eq('user_id', userId).order('fecha', { ascending: false })),
      q(supabase.from('bolsillos').select('*').eq('user_id', userId).order('created_at')),
    ]);
    setDeudas(d || []);
    setPagos(p || []);
    setBolsillos(b || []);
  }, [userId]);

  useEffect(() => {
    loadData().catch(e => setError('Error cargando datos: ' + e.message)).finally(() => setLoading(false));
  }, [loadData, setError]);

  const getTotalPagado = (deudaId) =>
    pagos.filter(p => p.deuda_id === deudaId).reduce((s, p) => s + Number(p.monto || 0), 0);

  const getPendiente = (deuda) =>
    Math.max(0, deuda.monto_original - getTotalPagado(deuda.id));

  // --- Crear deuda ---
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.monto_original) return;
    const ok = await run(() => q(supabase.from('deudas').insert({
      user_id: userId,
      nombre: form.nombre,
      acreedor: form.acreedor || null,
      icono: form.icono,
      monto_original: parseFloat(form.monto_original),
      cuota_mensual: form.cuota_mensual ? parseFloat(form.cuota_mensual) : 0,
      dia_vencimiento: form.dia_vencimiento ? parseInt(form.dia_vencimiento) : null,
      notas: form.notas || null,
      activa: true,
    })), loadData);
    if (ok) {
      setForm(formVacio);
      setShowForm(false);
    }
  };

  // --- Registrar pago ---
  const handlePago = async (e) => {
    e.preventDefault();
    if (!formPago.monto || !pagoModal) return;
    const monto = parseFloat(formPago.monto);
    const pendiente = getPendiente(pagoModal);
    if (monto > pendiente) {
      setError(`El pago no puede superar la deuda pendiente (${formatCLP(pendiente)})`);
      return;
    }
    const deuda = pagoModal;
    const ok = await run(async () => {
      // Si eligió una categoría, primero se crea la salida de dinero y el pago queda vinculado a ella
      const mov = formPago.bolsillo_id
        ? await crearMovimiento({
            userId,
            bolsilloId: formPago.bolsillo_id,
            tipo: 'egreso',
            monto,
            descripcion: `Pago deuda: ${deuda.nombre}`,
            fecha: formPago.fecha,
          })
        : null;
      try {
        await q(supabase.from('pagos_deuda').insert({
          user_id: userId,
          deuda_id: deuda.id,
          bolsillo_id: formPago.bolsillo_id || null,
          movimiento_id: mov?.id || null,
          monto,
          fecha: formPago.fecha,
          nota: formPago.nota || null,
        }));
      } catch (err) {
        await borrarMovimientoSimple(mov?.id);
        throw err;
      }
      await recalcularDeuda(deuda.id);
    }, loadData);
    if (ok) {
      setFormPago({ monto: '', bolsillo_id: '', fecha: hoyISO(), nota: '' });
      setPagoModal(null);
    }
  };

  // --- Editar deuda ---
  const handleEdit = (deuda) => {
    setEditingId(deuda.id);
    setEditForm({
      nombre: deuda.nombre,
      acreedor: deuda.acreedor || '',
      icono: deuda.icono || '💳',
      monto_original: deuda.monto_original,
      cuota_mensual: deuda.cuota_mensual || '',
      dia_vencimiento: deuda.dia_vencimiento || '',
      notas: deuda.notas || '',
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const id = editingId;
    const ok = await run(async () => {
      await q(supabase.from('deudas').update({
        nombre: editForm.nombre,
        acreedor: editForm.acreedor || null,
        icono: editForm.icono,
        monto_original: parseFloat(editForm.monto_original),
        cuota_mensual: editForm.cuota_mensual ? parseFloat(editForm.cuota_mensual) : 0,
        dia_vencimiento: editForm.dia_vencimiento ? parseInt(editForm.dia_vencimiento) : null,
        notas: editForm.notas || null,
      }).eq('id', id));
      await recalcularDeuda(id); // si cambió el monto, puede pasar a saldada o volver a activa
    }, loadData);
    if (ok) setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta deuda y todos sus pagos registrados?\n\nLas salidas de dinero ya registradas en tus categorías se mantienen.')) return;
    await run(() => q(supabase.from('deudas').delete().eq('id', id)), loadData);
  };

  const handleDeletePago = async (pago) => {
    const msg = pago.movimiento_id
      ? '¿Eliminar este pago?\n\nTambién se eliminará la salida de dinero de la categoría, así que el saldo vuelve a como estaba.'
      : '¿Eliminar este pago?';
    if (!window.confirm(msg)) return;
    await run(async () => {
      await q(supabase.from('pagos_deuda').delete().eq('id', pago.id));
      await borrarMovimientoSimple(pago.movimiento_id);
      await recalcularDeuda(pago.deuda_id);
    }, loadData);
  };

  const deudasActivas = deudas.filter(d => d.activa);
  const deudasSaldadas = deudas.filter(d => !d.activa);
  const totalPendiente = deudasActivas.reduce((s, d) => s + getPendiente(d), 0);
  const totalCuotaMensual = deudasActivas.reduce((s, d) => s + (d.cuota_mensual || 0), 0);

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  return (
    <div className="deudas-page">
      <div className="page-header">
        <div>
          <h2>Mis Deudas</h2>
          <p className="page-subtitle">Registra y controla lo que debes</p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(s => !s)}>
          {showForm ? 'Cancelar' : '+ Nueva deuda'}
        </button>
      </div>

      <ErrorBanner error={error} onClose={() => setError('')} />

      {/* Resumen */}
      {deudas.length > 0 && (
        <div className="summary-cards" style={{ marginBottom: 24 }}>
          <div className="card">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <h4>Deudas activas</h4>
              <p className="card-amount neutral">{deudasActivas.length}</p>
            </div>
          </div>
          <div className="card">
            <div className="card-icon">💸</div>
            <div className="card-content">
              <h4>Total pendiente</h4>
              <p className="card-amount negative">{formatCLP(totalPendiente)}</p>
            </div>
          </div>
          <div className="card">
            <div className="card-icon">📅</div>
            <div className="card-content">
              <h4>Cuotas este mes</h4>
              <p className="card-amount negative">{formatCLP(totalCuotaMensual)}</p>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>suma de todas las cuotas</span>
            </div>
          </div>
          <div className="card">
            <div className="card-icon">✅</div>
            <div className="card-content">
              <h4>Saldadas</h4>
              <p className="card-amount positive">{deudasSaldadas.length}</p>
            </div>
          </div>
        </div>
      )}

      {/* Formulario nueva deuda */}
      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16, fontSize: '1rem' }}>Registrar nueva deuda</h3>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <div className="form-group">
                <label>Nombre de la deuda</label>
                <input type="text" value={form.nombre}
                  onChange={e => setForm({ ...form, nombre: e.target.value })}
                  placeholder="Ej: Carro, Tarjeta BCI..." required />
              </div>
              <div className="form-group">
                <label>A quién le debes</label>
                <input type="text" value={form.acreedor}
                  onChange={e => setForm({ ...form, acreedor: e.target.value })}
                  placeholder="Ej: BancoEstado, María..." />
              </div>
              <div className="form-group">
                <label>Monto total que debes</label>
                <input type="number" value={form.monto_original}
                  onChange={e => setForm({ ...form, monto_original: e.target.value })}
                  placeholder="¿Cuánto debes en total?" required min="1" />
              </div>
              <div className="form-group">
                <label>Cuota mensual</label>
                <input type="number" value={form.cuota_mensual}
                  onChange={e => setForm({ ...form, cuota_mensual: e.target.value })}
                  placeholder="¿Cuánto pagas cada mes?" min="0" />
              </div>
              <div className="form-group">
                <label>Día de vencimiento</label>
                <input type="number" value={form.dia_vencimiento}
                  onChange={e => setForm({ ...form, dia_vencimiento: e.target.value })}
                  placeholder="Ej: 15" min="1" max="31" />
              </div>
              <div className="form-group">
                <label>Notas (opcional)</label>
                <input type="text" value={form.notas}
                  onChange={e => setForm({ ...form, notas: e.target.value })}
                  placeholder="Ej: Interés 1.5% mensual" />
              </div>
            </div>
            <div className="form-group" style={{ marginTop: 8 }}>
              <label>Icono</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                {ICONOS.map(ic => (
                  <button key={ic} type="button" onClick={() => setForm({ ...form, icono: ic })}
                    style={{ width: 38, height: 38, fontSize: '1.3rem', borderRadius: 8, cursor: 'pointer',
                      border: form.icono === ic ? '2px solid var(--primary)' : '2px solid var(--border)',
                      background: form.icono === ic ? 'var(--primary-xlight)' : 'white' }}>
                    {ic}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : 'Registrar deuda'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      {/* Lista vacía */}
      {deudas.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)', background: 'white', borderRadius: 12 }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>💳</div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Sin deudas registradas</p>
          <p style={{ fontSize: '0.9rem' }}>Registra tus deudas para ver cuánto debes en total y llevar el control de tus pagos.</p>
        </div>
      ) : (
        <>
          {/* Deudas activas */}
          {deudasActivas.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                Pendientes de pago
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {deudasActivas.map(deuda => {
                  const totalPagado = getTotalPagado(deuda.id);
                  const pendiente = getPendiente(deuda);
                  const pagosDeuda = pagos.filter(p => p.deuda_id === deuda.id);
                  const isExpanded = expandedId === deuda.id;

                  if (editingId === deuda.id) {
                    return (
                      <div key={deuda.id} className="card">
                        <form onSubmit={handleSaveEdit}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                            <div className="form-group"><label>Nombre</label>
                              <input type="text" value={editForm.nombre} onChange={e => setEditForm({ ...editForm, nombre: e.target.value })} required /></div>
                            <div className="form-group"><label>Acreedor</label>
                              <input type="text" value={editForm.acreedor} onChange={e => setEditForm({ ...editForm, acreedor: e.target.value })} /></div>
                            <div className="form-group"><label>Monto original</label>
                              <input type="number" value={editForm.monto_original} onChange={e => setEditForm({ ...editForm, monto_original: e.target.value })} required /></div>
                            <div className="form-group"><label>Cuota mensual</label>
                              <input type="number" value={editForm.cuota_mensual} onChange={e => setEditForm({ ...editForm, cuota_mensual: e.target.value })} /></div>
                            <div className="form-group"><label>Día vencimiento</label>
                              <input type="number" value={editForm.dia_vencimiento} onChange={e => setEditForm({ ...editForm, dia_vencimiento: e.target.value })} min="1" max="31" /></div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button type="submit" className="btn-primary" disabled={saving}>Guardar</button>
                            <button type="button" className="btn-secondary" onClick={() => setEditingId(null)}>Cancelar</button>
                          </div>
                        </form>
                      </div>
                    );
                  }

                  return (
                    <div key={deuda.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                      {/* Encabezado clickeable */}
                      <div style={{ padding: '16px 20px', cursor: 'pointer' }}
                        onClick={() => setExpandedId(isExpanded ? null : deuda.id)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontSize: '1.8rem', flexShrink: 0 }}>{deuda.icono || '💳'}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
                              <div>
                                <p style={{ fontWeight: 700, margin: 0, color: 'var(--text)' }}>{deuda.nombre}</p>
                                {deuda.acreedor && <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>{deuda.acreedor}</p>}
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <p style={{ margin: 0, fontWeight: 800, fontSize: '1.1rem', color: '#ef4444' }}>{formatCLP(pendiente)}</p>
                                <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>pendiente</p>
                              </div>
                            </div>
                            <ProgressBar pagado={totalPagado} total={deuda.monto_original} />
                            <div style={{ display: 'flex', gap: 16, fontSize: '0.8rem', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                              <span>Original: <strong>{formatCLP(deuda.monto_original)}</strong></span>
                              <span className="text-success">Pagado: <strong>{formatCLP(totalPagado)}</strong></span>
                              {deuda.cuota_mensual > 0 && (
                                <span>Cuota: <strong>{formatCLP(deuda.cuota_mensual)}</strong>
                                  {deuda.dia_vencimiento ? ` (día ${deuda.dia_vencimiento})` : ''}
                                </span>
                              )}
                            </div>
                            {deuda.notas && <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>{deuda.notas}</p>}
                          </div>
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', flexShrink: 0 }}>{isExpanded ? '▲' : '▼'}</span>
                        </div>
                      </div>

                      {/* Acciones y detalle expandido */}
                      {isExpanded && (
                        <div style={{ borderTop: '1px solid var(--border)', padding: '12px 20px', background: 'var(--bg)' }}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                            <button className="btn-primary" style={{ fontSize: '0.85rem', padding: '8px 14px' }}
                              onClick={() => { setPagoModal(deuda); setFormPago({ monto: deuda.cuota_mensual || '', bolsillo_id: bolsillos[0]?.id || '', fecha: hoyISO(), nota: '' }); }}>
                              💸 Registrar pago
                            </button>
                            <button className="btn-secondary" style={{ fontSize: '0.85rem', padding: '8px 12px' }} onClick={() => handleEdit(deuda)}>✎ Editar</button>
                            <button className="btn-secondary" style={{ fontSize: '0.85rem', padding: '8px 12px', color: 'var(--danger)' }} onClick={() => handleDelete(deuda.id)}>× Eliminar</button>
                          </div>

                          {pagosDeuda.length > 0 && (
                            <div>
                              <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Historial de pagos
                              </p>
                              {pagosDeuda.map(pago => {
                                const bolsillo = bolsillos.find(b => b.id === pago.bolsillo_id);
                                return (
                                  <div key={pago.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                                    <div>
                                      <span style={{ color: 'var(--text)' }}>{formatFecha(pago.fecha)}</span>
                                      {bolsillo && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>desde {bolsillo.nombre}</span>}
                                      {pago.nota && <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontStyle: 'italic' }}>· {pago.nota}</span>}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <span className="text-success" style={{ fontWeight: 600 }}>-{formatCLP(pago.monto)}</span>
                                      <button className="btn-icon-del small" onClick={() => handleDeletePago(pago)} title="Eliminar pago">×</button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Deudas saldadas */}
          {deudasSaldadas.length > 0 && (
            <div>
              <h3 style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                Saldadas ✅
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {deudasSaldadas.map(deuda => (
                  <div key={deuda.id} className="card" style={{ opacity: 0.7, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
                    <span style={{ fontSize: '1.4rem' }}>{deuda.icono}</span>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 600 }}>{deuda.nombre}</p>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        {formatCLP(deuda.monto_original)} — pagada en su totalidad
                      </p>
                    </div>
                    <button className="btn-secondary" style={{ fontSize: '0.78rem', padding: '4px 10px', color: 'var(--danger)' }}
                      onClick={() => handleDelete(deuda.id)}>× Eliminar</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal: Registrar pago */}
      {pagoModal && (
        <div className="modal-overlay" onClick={() => setPagoModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{pagoModal.icono} Pago a "{pagoModal.nombre}"</h3>
              <button className="modal-close" onClick={() => setPagoModal(null)}>×</button>
            </div>
            <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
              Pendiente: <strong style={{ color: '#ef4444' }}>{formatCLP(getPendiente(pagoModal))}</strong>
              {pagoModal.cuota_mensual > 0 && <span style={{ marginLeft: 12 }}>· Cuota habitual: <strong>{formatCLP(pagoModal.cuota_mensual)}</strong></span>}
            </div>
            <form onSubmit={handlePago}>
              <div className="form-group">
                <label>Monto del pago</label>
                <input type="number" value={formPago.monto}
                  onChange={e => setFormPago({ ...formPago, monto: e.target.value })}
                  placeholder="0" required min="1" autoFocus />
              </div>
              <div className="form-group">
                <label>¿De qué cuenta sale el dinero?</label>
                <select value={formPago.bolsillo_id}
                  onChange={e => setFormPago({ ...formPago, bolsillo_id: e.target.value })}>
                  <option value="">No descontar de ninguna cuenta</option>
                  {bolsillos.map(b => (
                    <option key={b.id} value={b.id}>{b.nombre}</option>
                  ))}
                </select>
                {formPago.bolsillo_id && (
                  <span className="field-hint">Se creará un egreso en esa categoría automáticamente</span>
                )}
              </div>
              <div className="form-group">
                <label>Fecha</label>
                <input type="date" value={formPago.fecha}
                  onChange={e => setFormPago({ ...formPago, fecha: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Nota (opcional)</label>
                <input type="text" value={formPago.nota}
                  onChange={e => setFormPago({ ...formPago, nota: e.target.value })}
                  placeholder="Ej: Cuota 3 de 24" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setPagoModal(null)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Guardando...' : '💸 Registrar pago'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Deudas;
